import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui';
import { formatMoney } from '@/utils/format';

/**
 * Card payment on the customer's order page.
 *
 * The card fields are Clover's own, rendered in iframes from Clover's SDK:
 * the number, expiry and CVV never exist in this page's DOM or in our API.
 * Clover hands back a one-time token, and our API uses it to pay the
 * ticket that is already open on the store's till.
 */

export interface PaymentOffer {
  enabled: boolean;
  status: 'UNPAID' | 'PROCESSING' | 'PAID';
  amountCents: number;
  currency: string;
  paidAt?: string;
  cardLast4?: string;
  cardBrand?: string;
  clover?: { publicToken: string; merchantId: string; sdkUrl: string };
}

interface CloverElement {
  mount(selector: string): void;
}
interface CloverSdk {
  elements(): { create(type: string, styles?: Record<string, unknown>): CloverElement };
  createToken(): Promise<{ token?: string; errors?: Record<string, string> }>;
}
declare global {
  interface Window {
    Clover?: new (publicToken: string, options: { merchantId: string }) => CloverSdk;
  }
}

const FIELD_STYLES = {
  body: { fontFamily: 'system-ui, sans-serif', fontSize: '16px' },
  input: { fontSize: '16px', padding: '10px 12px', border: '1px solid #d9d9d4', borderRadius: '6px', width: '100%' }
};

let sdkLoading: Promise<void> | null = null;
function loadSdk(url: string): Promise<void> {
  if (window.Clover) return Promise.resolve();
  if (!sdkLoading) {
    sdkLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        sdkLoading = null;
        reject(new Error('sdk'));
      };
      document.head.appendChild(script);
    });
  }
  return sdkLoading;
}

export function PublicOrderPayCard({
  token,
  offer,
  onPaid
}: {
  token: string;
  offer: PaymentOffer;
  onPaid: (paid: PaymentOffer) => void;
}) {
  const [phase, setPhase] = useState<'loading' | 'ready' | 'paying' | 'unavailable'>('loading');
  const [error, setError] = useState<string | null>(null);
  const clover = useRef<CloverSdk | null>(null);
  const money = (cents: number) => formatMoney(cents, offer.currency);

  useEffect(() => {
    if (offer.status !== 'UNPAID' || !offer.clover) return;
    const { publicToken, merchantId, sdkUrl } = offer.clover;
    let cancelled = false;

    loadSdk(sdkUrl)
      .then(() => {
        if (cancelled || !window.Clover) throw new Error('sdk');
        const sdk = new window.Clover(publicToken, { merchantId });
        const elements = sdk.elements();
        elements.create('CARD_NUMBER', FIELD_STYLES).mount('#clover-card-number');
        elements.create('CARD_DATE', FIELD_STYLES).mount('#clover-card-date');
        elements.create('CARD_CVV', FIELD_STYLES).mount('#clover-card-cvv');
        elements.create('CARD_POSTAL_CODE', FIELD_STYLES).mount('#clover-card-postal');
        clover.current = sdk;
        setPhase('ready');
      })
      .catch(() => !cancelled && setPhase('unavailable'));

    return () => {
      cancelled = true;
    };
  }, [offer.status, offer.clover]);

  if (offer.status === 'PAID') {
    return (
      <section className="mb-4 rounded-lg border border-jade bg-white p-4">
        <p className="font-semibold text-jade">Paid {money(offer.amountCents)}</p>
        <p className="text-small text-slate">
          {offer.cardBrand || offer.cardLast4
            ? `${offer.cardBrand ?? 'Card'}${offer.cardLast4 ? ` ending ${offer.cardLast4}` : ''}`
            : 'Payment received'}
          {offer.paidAt ? ` · ${new Date(offer.paidAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
        </p>
      </section>
    );
  }

  if (offer.status === 'PROCESSING') {
    return (
      <section className="mb-4 rounded-lg border border-rule bg-white p-4 text-small text-slate">
        A payment for this order is being processed. Refresh in a moment.
      </section>
    );
  }

  const pay = async () => {
    if (!clover.current) return;
    setError(null);
    setPhase('paying');
    try {
      const result = await clover.current.createToken();
      if (!result.token) {
        const first = result.errors ? Object.values(result.errors)[0] : undefined;
        throw new Error(first ?? 'Check the card details and try again.');
      }
      const res = await fetch(`/api/public/orders/${encodeURIComponent(token)}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: result.token })
      });
      const body = (await res.json().catch(() => ({}))) as {
        error?: { message?: string };
        amountCents?: number;
        paidAt?: string;
        cardLast4?: string;
        cardBrand?: string;
      };
      if (!res.ok) {
        throw new Error(
          res.status === 402
            ? `The card was declined${body.error?.message ? `: ${body.error.message}` : '.'}`
            : res.status === 409
              ? 'A payment for this order is already in progress. Refresh in a moment.'
              : 'Payment could not be completed. Nothing was charged. Please try again or pay at the store.'
        );
      }
      onPaid({
        enabled: true,
        status: 'PAID',
        amountCents: body.amountCents ?? offer.amountCents,
        currency: offer.currency,
        paidAt: body.paidAt,
        cardLast4: body.cardLast4,
        cardBrand: body.cardBrand
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment could not be completed.');
      setPhase('ready');
    }
  };

  return (
    <section className="mb-4 rounded-lg border border-rule bg-white p-4">
      <p className="mb-3 font-semibold text-ink">Pay online · {money(offer.amountCents)}</p>

      {phase === 'unavailable' ? (
        <p className="text-small text-slate">Online payment is not available right now. You can pay at the store.</p>
      ) : (
        <>
          <div className={phase === 'loading' ? 'opacity-60' : ''}>
            <label className="mb-1 block text-xs text-slate">Card number</label>
            <div id="clover-card-number" className="mb-2 min-h-[42px]" />
            <div className="mb-2 grid grid-cols-3 gap-2">
              <div>
                <label className="mb-1 block text-xs text-slate">Expiry</label>
                <div id="clover-card-date" className="min-h-[42px]" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate">CVV</label>
                <div id="clover-card-cvv" className="min-h-[42px]" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate">Postal code</label>
                <div id="clover-card-postal" className="min-h-[42px]" />
              </div>
            </div>
          </div>

          {error ? <p className="mb-2 text-small text-rust">{error}</p> : null}

          <Button
            className="w-full"
            onClick={pay}
            disabled={phase !== 'ready'}
            loading={phase === 'paying'}
          >
            {phase === 'loading' ? 'Loading secure card form…' : `Pay ${money(offer.amountCents)}`}
          </Button>
          <p className="mt-2 text-xs text-slate">Card details are handled by Clover and never reach {'the store’s'} website.</p>
        </>
      )}
    </section>
  );
}
